import React from "react";
import {
  Box,
  Typography,
  TextField,
  useTheme,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

/* ---------- helper fns ---------- */
const parenNumRx = /\(\s*\d+\s*\)/g;
const underscoreRx = /_{2,}/g;

// Only clean parenthetical numbers that don't match the questionId
const cleanText = (txt = "", questionId = null) => {
  if (!txt) return "";

  // If there's a questionId, only remove parentheses that don't match it
  if (questionId != null) {
    const expectedPattern = new RegExp(`\\(\\s*${questionId}\\s*\\)`, "g");
    // Keep the expected pattern, remove others
    return txt
      .replace(/\(\s*\d+\s*\)/g, (match) => {
        return match.match(expectedPattern) ? match : "";
      })
      .trim();
  }

  // If no questionId, remove all parenthetical numbers
  return txt.replace(parenNumRx, "").trim();
};

const splitForBlanks = (str, hasBlank) => {
  if (underscoreRx.test(str)) {
    underscoreRx.lastIndex = 0;
    const parts = str.split(underscoreRx);
    return parts.map((p, i) => ({ text: p, blank: i < parts.length - 1 }));
  }
  return [{ text: str, blank: hasBlank }];
};

const measureBlank = (raw) => {
  const m = raw.match(/_{2,}/);
  if (!m) return 160;
  const len = m[0].length;
  return Math.min(Math.max(len * 8, 120), 280); // clamp
};

/* ---------- component ---------- */
export default function MockNoteCompletion({
  noteCompletionData,
  handleAnswerChange,
  answers,
  instructions = "",
  questionMainHeading = "",
  zoomLevel = 100,
  tableData = null, // New prop for table structure
  questionsMap = {}, // Map of questionId to question text
  isFormCompletion = false, // New prop to identify form completion
}) {
  const theme = useTheme();
  const maroon = "#9D2235"; // used for heading + numbers
  const divider = theme.palette.divider;
  const bgCard = theme.palette.background.paper;
  const txtMain = theme.palette.text.primary;

  const sections = noteCompletionData?.sections || [];

  // Helper to render form completion in 2-column table layout
  const renderFormCompletion = () => {
    if (!sections[0]?.notes || sections[0].notes.length === 0) {
      return null;
    }

    const notes = sections[0].notes;

    return (
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          border: `2px solid ${divider}`,
          borderRadius: 2,
          overflow: "hidden",
        }}
      >
        <Table>
          <TableBody>
            {notes.map((note, idx) => {
              const qid = note?.questionId;
              const rawTxt = note?.text ?? "";
              const dispTxt = cleanText(rawTxt, qid);
              const hasValidQuestionId = Number.isInteger(qid) && qid > 0;

              // If no valid questionId, render as heading/label row without input
              if (!hasValidQuestionId) {
                return (
                  <TableRow
                    key={idx}
                    sx={{
                      "&:last-child td": { borderBottom: 0 },
                      bgcolor: "#F9F9F9",
                    }}
                  >
                    <TableCell
                      colSpan={2}
                      sx={{
                        py: 1.5,
                        px: 2.5,
                        fontWeight: 600,
                        color: txtMain,
                        fontSize: `calc((${zoomLevel / 100}) * 0.95rem)`,
                      }}
                    >
                      {dispTxt}
                    </TableCell>
                  </TableRow>
                );
              }

              const segments = splitForBlanks(dispTxt, true);
              const blankW = measureBlank(rawTxt);

              return (
                <TableRow
                  key={idx}
                  sx={{
                    "&:last-child td": { borderBottom: 0 },
                    "&:hover": { bgcolor: "#FAFAFA" },
                  }}
                >
                  {/* Label Column */}
                  <TableCell
                    sx={{
                      borderRight: `1px solid ${divider}`,
                      py: 2,
                      px: 2.5,
                      fontWeight: 600,
                      color: txtMain,
                      width: "30%",
                      verticalAlign: "middle",
                    }}
                  >
                    {note.heading || ""}
                  </TableCell>

                  {/* Input Column */}
                  <TableCell
                    sx={{
                      py: 2,
                      px: 2.5,
                      verticalAlign: "middle",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Box
                        component="span"
                        sx={{ fontWeight: 700, color: maroon }}
                      >
                        ({qid})
                      </Box>
                      {segments.map((seg, i) => (
                        <Box
                          key={i}
                          component="span"
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                          }}
                        >
                          {seg.text && (
                            <Typography
                              component="span"
                              sx={{
                                mr: seg.blank ? 1 : 0,
                                fontSize: `calc((${zoomLevel / 100}) * 1rem)`,
                              }}
                            >
                              {seg.text}
                            </Typography>
                          )}
                          {seg.blank && (
                            <TextField
                              variant="outlined"
                              size="small"
                              value={answers?.[qid] || ""}
                              onChange={(e) =>
                                handleAnswerChange?.(qid, e.target.value)
                              }
                              autoComplete="off"
                              sx={{
                                minWidth: blankW,
                                maxWidth: blankW,
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: 1.5,
                                  fontWeight: 600,
                                  px: 1,
                                  "& fieldset": { borderColor: divider },
                                  "&:hover fieldset": {
                                    borderColor: maroon,
                                  },
                                  "&.Mui-focused fieldset": {
                                    borderColor: maroon,
                                  },
                                },
                                "& input": {
                                  textAlign: "center",
                                  color: maroon,
                                },
                              }}
                            />
                          )}
                        </Box>
                      ))}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  // Helper to render table if tableData exists
  const renderTable = () => {
    if (!tableData) return null;

    const columns = tableData.columns || [];
    const rows = tableData.rows || [];

    // Parse columns (they might be JSON strings)
    let parsedColumns = columns.map((col) => {
      try {
        const cleaned = col.replace(/^\[?\n?"|"\n?\]?$/g, "").trim();
        return cleaned;
      } catch (e) {
        return col;
      }
    });

    let numCols = parsedColumns.length;
    // If columns data is missing or only has a single placeholder, auto-detect
    // from the row data to avoid chunkSize=0 infinite loop
    if (numCols <= 1 && rows.some((r) => r.length > 1)) {
      numCols = 2;
      while (parsedColumns.length < numCols) parsedColumns.push("");
    }

    // Render a single table cell — either a question-with-input or plain text
    const renderTableCell = (cell, colAbsIdx, rowKey) => {
      const cellStr = String(cell).trim();
      const questionId = parseInt(cellStr);
      const isLastCol = colAbsIdx === numCols - 1;
      const cellBorderSx = { borderRight: !isLastCol ? `1px solid ${divider}` : "none" };

      if (
        !isNaN(questionId) &&
        cellStr === String(questionId) &&
        questionsMap[questionId]
      ) {
        const questionText = questionsMap[questionId];
        const cleanedText = questionText.replace(/^•\s*/, "");
        const bulletPoints = cleanedText.split(/\n\n•\s*|\n•\s*/).filter(Boolean);

        return (
          <TableCell
            key={`cell-${rowKey}-${colAbsIdx}`}
            data-question-id={questionId}
            sx={{ ...cellBorderSx, py: 1.5, px: 1.5, verticalAlign: "top" }}
          >
            <Box sx={{ width: "100%" }}>
              <Typography
                component="div"
                sx={{ fontWeight: 700, color: maroon, fontSize: `calc((${zoomLevel / 100}) * 0.95rem)`, mb: 0.75 }}
              >
                {questionId}
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, width: "100%" }}>
                {bulletPoints.map((bulletText, bulletIdx) => {
                  const hasBlank = /_+/.test(bulletText);
                  if (hasBlank) {
                    const parts = bulletText.split(/_+/);
                    const blankCount = parts.length - 1;
                    // Split stored answer by comma for multi-blank questions
                    const storedValue = answers?.[questionId] || "";
                    const storedParts = storedValue.split(",");

                    return (
                      <Box key={bulletIdx} sx={{ display: "flex", alignItems: "center", gap: 0.5, width: "100%", flexWrap: "wrap" }}>
                        {parts.map((part, partIdx) => (
                          <React.Fragment key={partIdx}>
                            {part.trim() && (
                              <Typography component="span" sx={{ fontSize: `calc((${zoomLevel / 100}) * 0.875rem)`, lineHeight: 1.6 }}>
                                {part.trim()}
                              </Typography>
                            )}
                            {partIdx < parts.length - 1 && (
                              <TextField
                                variant="outlined"
                                size="small"
                                value={blankCount > 1 ? (storedParts[partIdx] || "") : storedValue}
                                onChange={(e) => {
                                  if (blankCount > 1) {
                                    const updated = [...storedParts];
                                    updated[partIdx] = e.target.value;
                                    handleAnswerChange?.(questionId, updated.join(","));
                                  } else {
                                    handleAnswerChange?.(questionId, e.target.value);
                                  }
                                }}
                                autoComplete="off"
                                sx={{
                                  width: 120,
                                  "& .MuiOutlinedInput-root": {
                                    borderRadius: 1,
                                    bgcolor: "#FAFAFA",
                                    height: 32,
                                    "& fieldset": { borderColor: "#E0E0E0", borderWidth: 1.5 },
                                    "&:hover fieldset": { borderColor: maroon, borderWidth: 1.5 },
                                    "&.Mui-focused": { bgcolor: "#FFFFFF", "& fieldset": { borderColor: maroon, borderWidth: 2 } },
                                  },
                                  "& input": { textAlign: "center", color: maroon, py: 0.5, px: 0.75, fontSize: `calc((${zoomLevel / 100}) * 0.85rem)`, fontWeight: 600 },
                                }}
                              />
                            )}
                          </React.Fragment>
                        ))}
                      </Box>
                    );
                  }
                  return (
                    <Typography key={bulletIdx} component="span" sx={{ fontSize: `calc((${zoomLevel / 100}) * 0.875rem)`, lineHeight: 1.6, display: "block" }}>
                      {bulletText.trim()}
                    </Typography>
                  );
                })}
              </Box>
            </Box>
          </TableCell>
        );
      }

      return (
        <TableCell
          key={`cell-${rowKey}-${colAbsIdx}`}
          sx={{
            ...cellBorderSx,
            py: 1.5,
            px: 1.5,
            fontSize: `calc((${zoomLevel / 100}) * 0.875rem)`,
            verticalAlign: "top",
            lineHeight: 1.6,
            whiteSpace: "normal",
            wordBreak: "break-word",
          }}
        >
          {cellStr || "\u00A0"}
        </TableCell>
      );
    };

    const rowSx = {
      "&:last-child td": { borderBottom: 0 },
      "&:hover": { bgcolor: "rgba(157, 34, 53, 0.02)" },
      transition: "background-color 0.2s ease",
    };

    return (
      <TableContainer
        component={Paper}
        elevation={0}
        sx={{ border: `2px solid ${divider}`, borderRadius: 2, width: "100%", boxShadow: "0 2px 8px rgba(0,0,0,0.05)" }}
      >
        <Table sx={{ width: "100%", tableLayout: "fixed" }}>
          <TableHead>
            <TableRow sx={{ bgcolor: "#F5F5F5" }}>
              {parsedColumns.map((col, idx) => (
                <TableCell
                  key={idx}
                  sx={{
                    fontWeight: 700,
                    color: maroon,
                    fontSize: `calc((${zoomLevel / 100}) * 0.95rem)`,
                    borderRight: idx < parsedColumns.length - 1 ? `1px solid ${divider}` : "none",
                    py: 1.5,
                    px: 1.5,
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                  }}
                >
                  {col}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {(() => {
              const tableRows = [];
              rows.forEach((row, rowIdx) => {
                if (row.length <= numCols) {
                  // Normal row — pad to numCols if needed
                  const paddedRow = [...row];
                  while (paddedRow.length < numCols) paddedRow.push("");
                  tableRows.push(
                    <TableRow key={rowIdx} sx={rowSx}>
                      {paddedRow.map((cell, cellIdx) => renderTableCell(cell, cellIdx, rowIdx))}
                    </TableRow>
                  );
                } else {
                  // Row has more cells than columns.
                  // Treat first cell as a group label spanning sub-rows;
                  // remaining cells are chunked into groups of (numCols - 1).
                  const groupLabel = String(row[0]).trim();
                  const remaining = row.slice(1);
                  const chunkSize = numCols - 1;
                  const chunks = [];
                  for (let i = 0; i < remaining.length; i += chunkSize) {
                    const chunk = remaining.slice(i, i + chunkSize);
                    while (chunk.length < chunkSize) chunk.push("");
                    chunks.push(chunk);
                  }
                  chunks.forEach((chunk, chunkIdx) => {
                    tableRows.push(
                      <TableRow key={`${rowIdx}-${chunkIdx}`} sx={rowSx}>
                        {chunkIdx === 0 && (
                          <TableCell
                            rowSpan={chunks.length}
                            sx={{
                              borderRight: `1px solid ${divider}`,
                              py: 1.5,
                              px: 1.5,
                              fontSize: `calc((${zoomLevel / 100}) * 0.875rem)`,
                              verticalAlign: "top",
                              lineHeight: 1.6,
                              fontWeight: 600,
                              wordBreak: "break-word",
                            }}
                          >
                            {groupLabel || "\u00A0"}
                          </TableCell>
                        )}
                        {chunk.map((cell, chunkCellIdx) =>
                          renderTableCell(cell, chunkCellIdx + 1, `${rowIdx}-${chunkIdx}`)
                        )}
                      </TableRow>
                    );
                  });
                }
              });
              return tableRows;
            })()}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box
      sx={{
        width: "100%",
        userSelect: "text", // Enable text selection
        "& *": { userSelect: "text" }, // Ensure all children are selectable
      }}
    >
      {/* Display instructions if provided - matching MCQ design */}
      {instructions && (
        <Paper
          elevation={0}
          sx={{
            mb: 1.5,
            p: 1.25,
            border: `1px solid ${divider}`,
            borderRadius: 1.5,
            bgcolor: "#fff",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 0.5 }}>
            <InfoOutlinedIcon sx={{ fontSize: 20, color: maroon, mr: 0.75 }} />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: maroon,
                fontSize: `calc((${zoomLevel / 100}) * 1rem)`,
              }}
            >
              Instruction
            </Typography>
          </Box>
          <Typography
            variant="body2"
            sx={{
              color: txtMain,
              lineHeight: 1.4,
              whiteSpace: "pre-line",
              userSelect: "text",
              cursor: "text",
              fontSize: `calc((${zoomLevel / 100}) * 0.8125rem)`,
            }}
          >
            {instructions}
          </Typography>
        </Paper>
      )}

      {/* Display questionMainHeading if provided */}
      {questionMainHeading && (
        <Paper
          elevation={0}
          sx={{
            mb: 1.5,
            p: 1.5,
            border: `2px solid ${divider}`,
            borderRadius: 2,
            bgcolor: "#fff",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: maroon,
              textAlign: "center",
              userSelect: "text",
              cursor: "text",
              fontSize: `calc((${zoomLevel / 100}) * 1.125rem)`,
            }}
          >
            {questionMainHeading}
          </Typography>
        </Paper>
      )}

      {/* Render table if tableData exists, form layout for form completion, otherwise render regular notes */}
      {tableData ? (
        renderTable()
      ) : isFormCompletion ? (
        renderFormCompletion()
      ) : (
        <>
          {sections.map((section, sIdx) => (
            <Box
              key={sIdx}
              sx={{
                my: 1,
                p: 1.5,
                border: `1px solid ${divider}`,
                borderRadius: 2,
                bgcolor: bgCard,
                userSelect: "text", // Enable text selection on container
                "& *": { userSelect: "text" }, // Ensure all children are selectable
              }}
            >
              {/* ----- section heading w/ underline ----- */}
              {section.heading && (
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 700,
                    mb: 1,
                    pb: 0.5,
                    color: txtMain,
                    borderBottom: `1px solid ${divider}`,
                    userSelect: "text", // Enable text selection
                    cursor: "text", // Show text cursor
                    fontSize: `calc((${zoomLevel / 100}) * 0.9375rem)`,
                  }}
                >
                  {section.heading}
                </Typography>
              )}

              {/* ----- notes grouped by questionHeading ----- */}
              {(() => {
                // Group notes by their heading (questionHeading / left column label)
                // Fallback to questionMainHeading when questionHeading is missing
                // Support both `section.notes` and `section.questions` (API uses
                // `questions` in listeningTest payloads).
                const grouped = {};
                const notesArray = section.notes || section.questions || [];
                console.log("MockNoteCompletion - notesArray:", notesArray);
                notesArray.forEach((note) => {
                  const key = (
                    note?.heading ||
                    note?.questionMainHeading ||
                    ""
                  ).trim();
                  if (!grouped[key]) grouped[key] = [];
                  grouped[key].push(note);
                });
                console.log("MockNoteCompletion - grouped:", grouped);

                return Object.entries(grouped).map(
                  ([groupHeading, groupNotes], gIdx) => {
                    const groupRange =
                      groupNotes?.[0]?.questionRange ||
                      section.sectionQuestionRange ||
                      "";
                    // Note: groupInstr is NOT displayed here because instructions are shown
                    // once at the top level in ListeningSection.js to avoid repetition

                    // Check if groupHeading is the same as section heading (to avoid duplication)
                    const sectionHeading = (section.heading || "").trim();
                    const isDuplicateHeading =
                      groupHeading &&
                      sectionHeading &&
                      groupHeading.toLowerCase() ===
                        sectionHeading.toLowerCase();

                    return (
                      <Box
                        key={gIdx}
                        sx={{
                          // the UI shows inline blanks without an enclosing box.
                          border: "none",
                          borderRadius: 2,
                          bgcolor: bgCard,
                        }}
                      >
                        {/* Group heading (renders the questionHeading on left in screenshots)
                    Only render when a real heading exists and it's not a duplicate of section heading
                */}
                        {groupHeading && !isDuplicateHeading ? (
                          <>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 700,
                                mb: 0.35,
                                color: txtMain,
                                fontSize: `calc((${zoomLevel / 100}) * 0.8125rem)`,
                              }}
                            >
                              {groupHeading}
                            </Typography>
                            {groupRange ? (
                              <Typography
                                variant="body2"
                                sx={{
                                  color: txtMain,
                                  mb: 0.35,
                                  fontSize: `calc((${zoomLevel / 100}) * 0.8125rem)`,
                                }}
                              >
                                {groupRange}
                              </Typography>
                            ) : null}
                          </>
                        ) : null}

                        {/* Render each note in the group (plain text or input) */}
                        {groupNotes.map((note, nIdx) => {
                          const qid = note?.questionId;
                          const rawTxt = note?.text ?? "";
                          const dispTxt = cleanText(rawTxt, qid);
                          const hasValidQuestionId =
                            Number.isInteger(qid) && qid > 0;
                          const imageUrl = note?.imageUrl;

                          if (!hasValidQuestionId) {
                            return (
                              <Typography
                                key={nIdx}
                                component="div"
                                variant="body1"
                                sx={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  alignItems: "center",
                                  lineHeight: 1.8,
                                  mb: 1.5,
                                  userSelect: "text",
                                  fontSize: `calc((${zoomLevel / 100}) * 1rem)`,
                                }}
                              >
                                {dispTxt}
                              </Typography>
                            );
                          }

                          const segments = splitForBlanks(dispTxt, true);
                          const blankW = measureBlank(rawTxt);

                          return (
                            <Box
                              key={nIdx}
                              data-question-id={qid}
                              sx={{
                                mb: 1.5,
                                lineHeight: 2,
                                userSelect: "text",
                                fontSize: `calc((${zoomLevel / 100}) * 1rem)`,
                              }}
                            >
                              <Box
                                component="span"
                                sx={{
                                  mr: 1,
                                  fontWeight: 700,
                                  color: maroon,
                                  display: "inline",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                ({qid})
                              </Box>
                              {(() => {
                                const blankCount = segments.filter((s) => s.blank).length;
                                const storedValue = answers?.[qid] || "";
                                const storedParts = storedValue.split(",");
                                let blankCounter = 0;
                                return segments.map((seg, i) => {
                                  const myBlankIdx = seg.blank ? blankCounter++ : -1;
                                  return (
                                    <React.Fragment key={i}>
                                      {seg.text && (
                                        <Box
                                          component="span"
                                          sx={{ display: "inline" }}
                                        >
                                          {seg.text}
                                        </Box>
                                      )}
                                      {seg.blank && (
                                        <TextField
                                          variant="outlined"
                                          size="small"
                                          value={
                                            blankCount > 1
                                              ? storedParts[myBlankIdx] || ""
                                              : storedValue
                                          }
                                          onChange={(e) => {
                                            if (blankCount > 1) {
                                              const updated = [...storedParts];
                                              updated[myBlankIdx] = e.target.value;
                                              handleAnswerChange?.(qid, updated.join(","));
                                            } else {
                                              handleAnswerChange?.(qid, e.target.value);
                                            }
                                          }}
                                          autoComplete="off"
                                          sx={{
                                            mx: 0.75,
                                            width: blankW,
                                            display: "inline-flex",
                                            verticalAlign: "middle",
                                            "& .MuiOutlinedInput-root": {
                                              borderRadius: 3,
                                              fontWeight: 600,
                                              px: 1,
                                              height: 34,
                                              "& fieldset": {
                                                borderColor: divider,
                                              },
                                              "&:hover fieldset": {
                                                borderColor: maroon,
                                              },
                                              "&.Mui-focused fieldset": {
                                                borderColor: maroon,
                                              },
                                            },
                                            "& input": {
                                              textAlign: "center",
                                              color: maroon,
                                              py: 0.5,
                                            },
                                          }}
                                        />
                                      )}
                                    </React.Fragment>
                                  );
                                });
                              })()}
                              {imageUrl && (
                                <Box
                                  sx={{
                                    mt: 1,
                                    p: 1.5,
                                    bgcolor: "#fafafa",
                                    borderRadius: 1.5,
                                    border: `1px solid ${divider}`,
                                  }}
                                >
                                  <img
                                    src={imageUrl}
                                    alt={`Question ${qid}`}
                                    style={{
                                      width: "100%",
                                      maxWidth: "600px",
                                      height: "auto",
                                      display: "block",
                                      borderRadius: "8px",
                                      boxShadow: "0 2px 4px rgba(0,0,0,0.08)",
                                    }}
                                  />
                                </Box>
                              )}
                            </Box>
                          );
                        })}
                      </Box>
                    );
                  },
                );
              })()}
            </Box>
          ))}
        </>
      )}
    </Box>
  );
}
